import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../config/api';
import './PersonsViewer.css';

function PersonsViewer() {
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('-pipedriveUpdatedAt');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPersons, setTotalPersons] = useState(0);

  // Persona seleccionada para modal
  const [selectedPerson, setSelectedPerson] = useState(null);

  // Ref para controlar el debounce
  const debounceTimer = useRef(null);

  // Debounce del término de búsqueda
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchPersons();
  }, [sortBy, currentPage, debouncedSearchTerm]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Error obteniendo estadísticas:', err);
    }
  };

  const fetchPersons = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
        sort: sortBy
      };

      let response;
      if (debouncedSearchTerm.trim()) {
        response = await api.get('/api/persons/search', {
          params: { ...params, q: debouncedSearchTerm }
        });
      } else {
        response = await api.get('/api/persons', { params });
      }

      setPersons(response.data.data);
      setTotalPersons(response.data.pagination.total);
    } catch (err) {
      setError('Error cargando personas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalPersons / itemsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPrimaryEmail = (emails) => {
    if (!emails || emails.length === 0) return '-';
    const primary = emails.find(e => e.primary);
    return primary ? primary.value : emails[0].value;
  };

  const getPrimaryPhone = (phones) => {
    if (!phones || phones.length === 0) return '-';
    const primary = phones.find(p => p.primary);
    return primary ? primary.value : phones[0].value;
  };

  const getDealStatusBadge = (status) => {
    const badges = {
      won: { label: 'Ganado', icon: '✓', class: 'deal-status-won' },
      lost: { label: 'Perdido', icon: '✗', class: 'deal-status-lost' },
      open: { label: 'Abierto', icon: '○', class: 'deal-status-open' }
    };
    const badge = badges[status] || { label: status, icon: '?', class: '' };
    return (
      <span className={`deal-status-badge ${badge.class}`}>
        {badge.icon} {badge.label}
      </span>
    );
  };

  return (
    <div className="persons-viewer">
      {/* Estadísticas */}
      {stats && (
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-label">Total Personas</span>
            <span className="stat-value">{stats.persons.total}</span>
          </div>
        </div>
      )}

      {/* Controles */}
      <div className="controls-bar">
        <div className="control-group">
          <input
            type="text"
            placeholder="Buscar en toda la base de datos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="control-group">
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="-pipedriveUpdatedAt">Más recientes</option>
            <option value="pipedriveUpdatedAt">Más antiguos</option>
            <option value="name">Nombre A-Z</option>
            <option value="-name">Nombre Z-A</option>
          </select>

          <button onClick={fetchPersons} className="btn-refresh" title="Recargar">
            🔄
          </button>
        </div>
      </div>

      {/* Lista de Personas */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando personas...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={fetchPersons} className="btn btn-secondary">
            Reintentar
          </button>
        </div>
      ) : persons.length === 0 ? (
        <div className="empty-state">
          <p>No se encontraron personas</p>
        </div>
      ) : (
        <>
          <div className="persons-grid">
            {persons.map((person) => (
              <div key={person._id} className="person-card">
                <div className="person-header">
                  <div className="person-avatar">
                    {person.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="person-info">
                    <h3>{person.name}</h3>
                    {person.orgName && (
                      <p className="person-org">{person.orgName}</p>
                    )}
                  </div>
                </div>

                <div className="person-details">
                  <div className="detail-row">
                    <span className="detail-icon">📧</span>
                    <span className="detail-text">{getPrimaryEmail(person.emails)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-icon">📞</span>
                    <span className="detail-text">{getPrimaryPhone(person.phones)}</span>
                  </div>
                  {person.ownerName && (
                    <div className="detail-row">
                      <span className="detail-icon">👤</span>
                      <span className="detail-text">{person.ownerName}</span>
                    </div>
                  )}
                </div>

                <div className="person-footer">
                  <span className="person-id">ID: {person.pipedriveId}</span>
                  <button
                    onClick={() => setSelectedPerson(person)}
                    className="btn-view-person"
                  >
                    Ver más
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Paginación */}
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn-page"
            >
              ← Anterior
            </button>

            <span className="page-info">
              Página {currentPage} de {totalPages} ({totalPersons} personas)
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Siguiente →
            </button>
          </div>
        </>
      )}

      {/* Modal de detalle */}
      {selectedPerson && (
        <div className="modal-overlay" onClick={() => setSelectedPerson(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-section">
                <div className="person-avatar-large">
                  {selectedPerson.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2>{selectedPerson.name}</h2>
                  {selectedPerson.orgName && (
                    <p className="modal-org">{selectedPerson.orgName}</p>
                  )}
                </div>
              </div>
              <button onClick={() => setSelectedPerson(null)} className="btn-close">
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>ID Pipedrive:</strong>
                  <span>{selectedPerson.pipedriveId}</span>
                </div>
                <div className="detail-item">
                  <strong>Nombre completo:</strong>
                  <span>{selectedPerson.name}</span>
                </div>
                {selectedPerson.firstName && (
                  <div className="detail-item">
                    <strong>Nombre:</strong>
                    <span>{selectedPerson.firstName}</span>
                  </div>
                )}
                {selectedPerson.lastName && (
                  <div className="detail-item">
                    <strong>Apellido:</strong>
                    <span>{selectedPerson.lastName}</span>
                  </div>
                )}
                {selectedPerson.orgName && (
                  <div className="detail-item">
                    <strong>Organización:</strong>
                    <span>{selectedPerson.orgName}</span>
                  </div>
                )}
                {selectedPerson.ownerName && (
                  <div className="detail-item">
                    <strong>Propietario:</strong>
                    <span>{selectedPerson.ownerName}</span>
                  </div>
                )}
                <div className="detail-item">
                  <strong>Estado:</strong>
                  <span>{!selectedPerson.isDeleted ? '✓ Activo' : '✗ Eliminado'}</span>
                </div>
                {selectedPerson.jobTitle && (
                  <div className="detail-item">
                    <strong>Cargo:</strong>
                    <span>{selectedPerson.jobTitle}</span>
                  </div>
                )}
                {selectedPerson.birthday && (
                  <div className="detail-item">
                    <strong>Cumpleaños:</strong>
                    <span>{selectedPerson.birthday}</span>
                  </div>
                )}
                <div className="detail-item">
                  <strong>Creado:</strong>
                  <span>{formatDate(selectedPerson.pipedriveAddedAt)}</span>
                </div>
                <div className="detail-item">
                  <strong>Actualizado:</strong>
                  <span>{formatDate(selectedPerson.pipedriveUpdatedAt)}</span>
                </div>
              </div>

              {selectedPerson.customFields && Object.keys(selectedPerson.customFields).length > 0 && (
                <div className="detail-section">
                  <strong>Información Personal:</strong>
                  <div className="detail-grid">
                    {selectedPerson.customFields['0cffd6c2062204ed6a780240f5748acd2bd5f4dd'] && (
                      <div className="detail-item">
                        <strong>CUIT:</strong>
                        <span>{selectedPerson.customFields['0cffd6c2062204ed6a780240f5748acd2bd5f4dd']}</span>
                      </div>
                    )}
                    {selectedPerson.customFields['780035331bc7f782b7284861de7a96f488dd9d9c'] && (
                      <div className="detail-item">
                        <strong>DNI:</strong>
                        <span>{selectedPerson.customFields['780035331bc7f782b7284861de7a96f488dd9d9c']}</span>
                      </div>
                    )}
                    {selectedPerson.customFields['311111165ef4612964702fb9a664f96c837727dc'] && (
                      <div className="detail-item">
                        <strong>Fecha Nacimiento:</strong>
                        <span>{selectedPerson.customFields['311111165ef4612964702fb9a664f96c837727dc'].split('-').reverse().join('-')}</span>
                      </div>
                    )}
                  </div>
                  {selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9'] && (
                    <div style={{ marginTop: '1rem' }}>
                      {typeof selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9'] === 'object' ? (
                        <>
                          <div className="detail-item">
                            <strong>Dirección:</strong>
                            <span>{selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9'].formatted_address}</span>
                          </div>
                          <div className="detail-grid" style={{ marginTop: '0.5rem' }}>
                            {selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9'].locality && (
                              <div className="detail-item">
                                <strong>Localidad:</strong>
                                <span>{selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9'].locality}</span>
                              </div>
                            )}
                            {selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9'].admin_area_level_1 && (
                              <div className="detail-item">
                                <strong>Provincia:</strong>
                                <span>{selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9'].admin_area_level_1}</span>
                              </div>
                            )}
                            {selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9'].postal_code && (
                              <div className="detail-item">
                                <strong>Código Postal:</strong>
                                <span>{selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9'].postal_code}</span>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="detail-item">
                          <strong>Dirección:</strong>
                          <span>{selectedPerson.customFields['8f2a1ffaac63d0078d3f9c57ee4dc8925c8a13d9']}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedPerson.emails && selectedPerson.emails.length > 0 && (
                <div className="detail-section">
                  <strong>Emails:</strong>
                  <div className="contact-list">
                    {selectedPerson.emails.map((email, idx) => (
                      <div key={idx} className="contact-item">
                        <span className="contact-icon">📧</span>
                        <div className="contact-info">
                          <span className="contact-value">{email.value}</span>
                          <span className="contact-label">
                            {email.label}
                            {email.primary && ' (Principal)'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedPerson.phones && selectedPerson.phones.length > 0 && (
                <div className="detail-section">
                  <strong>Teléfonos:</strong>
                  <div className="contact-list">
                    {selectedPerson.phones.map((phone, idx) => (
                      <div key={idx} className="contact-item">
                        <span className="contact-icon">📞</span>
                        <div className="contact-info">
                          <span className="contact-value">{phone.value}</span>
                          <span className="contact-label">
                            {phone.label}
                            {phone.primary && ' (Principal)'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedPerson.deals && selectedPerson.deals.length > 0 && (
                <div className="detail-section">
                  <strong>Deals Asociados ({selectedPerson.deals.length}):</strong>
                  <div className="deals-list">
                    {selectedPerson.deals.map((deal, idx) => (
                      <div key={idx} className="deal-item">
                        <div className="deal-header">
                          <div className="deal-title">{deal.title}</div>
                          {getDealStatusBadge(deal.status)}
                        </div>
                        {deal.customFields && deal.customFields['6b5f67be74dbf11c40b98fe5c38c09c66519c59d'] && (
                          <div className="deal-patente">
                            🚗 {deal.customFields['6b5f67be74dbf11c40b98fe5c38c09c66519c59d']}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PersonsViewer;
